class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', (e) => {
        FacetFiltersForm.updateInstantSectionCounts(facetForm);
        this.debouncedOnSubmit(e);
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const grid = document.getElementById('ProductGridContainer');
    const collectionEl = grid?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderResultsCount(html);

        renderCellSwipers();
        hideGridSectionsIfEmpty();

        if (typeof initializeScrollAnimationTrigger === 'function') {
          const grid = document.getElementById('ProductGridContainer');
          initializeScrollAnimationTrigger(grid || document);
        }
      })
      .then(() => {
        document.body.dispatchEvent(new CustomEvent('gridFiltered'));
      })
      .catch((err) => {
        console.error('[FacetFiltersForm] renderSectionFromFetch failed', err);
        document.body.dispatchEvent(new CustomEvent('gridFiltered'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderResultsCount(html);
    document.body.dispatchEvent(new CustomEvent('gridFiltered'));
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderResultsCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const newCountSpan = parsedHTML.querySelector('.ie-facets-results-button span');
    if (!newCountSpan) return;

    const newValue = newCountSpan.textContent.trim();

    const currentSpans = document.querySelectorAll('.ie-facets-results-button span');
    if (!currentSpans.length) return;

    currentSpans.forEach((span) => {
      span.textContent = newValue;
    });
  }

  static updateInstantSectionCounts(root) {
    const scope = root || document;
    const form = scope.matches?.('form') ? scope : scope.querySelector?.('form') || document.getElementById('FacetFiltersFormDrawer');
    if (!form) return;

    form.querySelectorAll('.js-filter').forEach((filterEl) => {
      const header = filterEl.querySelector('[onclick*="accordionFilterLogic"]');
      if (!header) return;

      const labelEl = header.querySelector('span');
      if (!labelEl) return;

      if (!labelEl.dataset.ieBaseLabel) {
        labelEl.dataset.ieBaseLabel = labelEl.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }

      let count = filterEl.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length;

      if (count === 0) {
        const hasValueInput = Array.from(
          filterEl.querySelectorAll('input:not([type="checkbox"]):not([type="radio"])')
        ).some((inp) => (inp.value ?? '').toString().trim() !== '');

        const hasSelectValue = Array.from(filterEl.querySelectorAll('select')).some(
          (sel) => (sel.value ?? '').toString().trim() !== ''
        );

        if (hasValueInput || hasSelectValue) count = 1;
      }

      const base = labelEl.dataset.ieBaseLabel;
      labelEl.textContent = count > 0 ? `${base} (${count})` : base;
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersFormDrawer .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target.closest('.js-filter')) {
      FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    }

    if (typeof loadFacetsIcons === 'function') {
      loadFacetsIcons();
    }

    const form = document.getElementById('FacetFiltersFormDrawer');
    FacetFiltersForm.updateInstantSectionCounts(form);
    FacetFiltersForm.openAppliedAccordionPanels();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const newHeader = html.querySelector('.ie-facets-header');
    const currentHeader = document.querySelector('.ie-facets-header');

    if (newHeader && currentHeader) {
      currentHeader.innerHTML = newHeader.innerHTML;
    }

    const mobileForm = document.getElementById('FacetFiltersFormDrawer');
    if (mobileForm && mobileForm.closest('menu-drawer') && typeof mobileForm.closest('menu-drawer').bindEvents === 'function') {
      mobileForm.closest('menu-drawer').bindEvents();
    }

    FacetFiltersForm.initHeaderInteractions();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  ieSwitchAvailability(state, scope = document){
    const availabilityTrue = scope.querySelectorAll(`input[name="filter.v.availability"][value="1"]`);

    availabilityTrue.forEach(el => {
      if(state) return el.checked = true;
      return el.checked = false;
    })
  }

  ieCustomFilterLogic(event){
    const sizeOptionNames = [
      'filter.v.option.velikost',
      'filter.v.option.veľkosť',
      'filter.v.option.size',
      'filter.v.option.konfektionsgröße',
    ];
    const isSizeFilter = sizeOptionNames.includes(event.target.name);
    const enforcesAvailability = event.target.dataset.enforceAvailability === 'true';

    if (!isSizeFilter && !enforcesAvailability) return;

    const form = event.target.closest('form') || document;
    const hasSelectedSize = Array.from(form.querySelectorAll('input[type="checkbox"]:checked')).some((input) =>
      sizeOptionNames.includes(input.name) || input.dataset.enforceAvailability === 'true'
    );

    this.ieSwitchAvailability(hasSelectedSize, form);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    this.ieCustomFilterLogic(event);

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const currentForm = event.target.closest('form');
    const currentFormId = currentForm ? currentForm.id : null;

    sortFilterForms.forEach((form) => {
      if (form.id === 'FacetFiltersFormDrawer') {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  static accordionFilterLogic(clickedElement) {
    clickedElement.classList.toggle("active-filter");
    const panel = clickedElement.nextElementSibling;

    if (panel.classList.contains("tw-hidden")) {
      panel.classList.remove("tw-hidden");
      panel.classList.add("tw-flex");
    } else {
      panel.classList.remove("tw-flex");
      panel.classList.add("tw-hidden");
    }
  }

  static bindSummaryOpenGuards() {
    const disclosure = document.querySelector('#ie-grid-header-filter .mobile-facets__disclosure');
    if (!disclosure) return;

    const summary = disclosure.querySelector(':scope > summary');
    if (!summary) return;

    if (summary.dataset.ieBound === '1') return;
    summary.dataset.ieBound = '1';

    summary.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('a')) {
          e.stopPropagation();
          return;
        }

        const isChip = !!e.target.closest('.js-filter-chip');
        const isMobileBar = !!e.target.closest('.ie-facets-mobile');

        if (disclosure.open) {
          if (isChip || isMobileBar) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          return;
        }

        if (isChip || isMobileBar) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }

  static hasAnyAppliedFilters(formEl) {
    if (!formEl) return false;

    if (formEl.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked')) return true;

    const inputs = formEl.querySelectorAll('input:not([type="checkbox"]):not([type="radio"])');
    for (const input of inputs) {
      const v = (input.value ?? '').toString().trim();
      if (v !== '') return true;
    }

    const selects = formEl.querySelectorAll('select');
    for (const s of selects) {
      if (s.value && s.value !== '') return true;
    }

    return false;
  }

  static openAppliedAccordionPanels() {
    const drawer = document.querySelector('#ie-grid-header-filter menu-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('.mobile-facets__details').forEach((section) => {
      const header = section.querySelector('[onclick*="accordionFilterLogic"]');
      if (!header) return;

      const panel = header.nextElementSibling;
      if (!panel) return;

      const shouldBeOpen = FacetFiltersForm.hasAnyAppliedFilters(section);
      const isOpen = !panel.classList.contains('tw-hidden');

      if (shouldBeOpen && !isOpen) {
        FacetFiltersForm.accordionFilterLogic(header);
      }
    });
  }

  static collapseAllAccordionPanels() {
    const drawer = document.querySelector('#ie-grid-header-filter menu-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('.mobile-facets__details').forEach((section) => {
      const header = section.querySelector('[onclick*="accordionFilterLogic"]');
      if (!header) return;

      const panel = header.nextElementSibling;
      if (!panel) return;

      const isOpen = !panel.classList.contains('tw-hidden');

      if (isOpen) {
        FacetFiltersForm.accordionFilterLogic(header);
      }
    });
  }

  static bindHeaderLinkGuards() {
    const header = document.querySelector('#ie-grid-header-filter .ie-facets-header');
    if (!header) return;

    header.querySelectorAll('a').forEach((a) => {
      if (a.dataset.ieStop === '1') return;
      a.dataset.ieStop = '1';
      a.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
  }

  static bindDrawerResetOnClose() {
    const disclosure = document.querySelector('#ie-grid-header-filter .mobile-facets__disclosure');
    if (!disclosure) return;

    if (disclosure.dataset.ieResetBound === '1') return;
    disclosure.dataset.ieResetBound = '1';

    disclosure.addEventListener('toggle', () => {
      const form = document.getElementById('FacetFiltersFormDrawer');

      if (disclosure.open) {
        disclosure.dataset.ieHadAppliedOnOpen = FacetFiltersForm.hasAnyAppliedFilters(form) ? '1' : '0';
        const openedByChip = disclosure.dataset.ieOpenedByChip === '1';

        if (!openedByChip) {
          FacetFiltersForm.openAppliedAccordionPanels();
        }
        FacetFiltersForm.updateInstantSectionCounts(form);
        if (openedByChip) {
          delete disclosure.dataset.ieOpenedByChip;
        }
        return;
      }

      const hadAppliedOnOpen = disclosure.dataset.ieHadAppliedOnOpen === '1';
      const hasAppliedNow = FacetFiltersForm.hasAnyAppliedFilters(form);

      if (!hadAppliedOnOpen && !hasAppliedNow) {
        FacetFiltersForm.collapseAllAccordionPanels();
      }
    });
  }



  static initHeaderInteractions() {
    FacetFiltersForm.bindSummaryOpenGuards();
    FacetFiltersForm.bindHeaderLinkGuards();
    FacetFiltersForm.bindDrawerResetOnClose();
    FacetFiltersForm.initFilterChips();
  }

  static initFilterChips() {
    const chips = document.querySelectorAll('.js-filter-chip');

    chips.forEach((chip) => {
      if (chip.dataset.ieBound === '1') return;
      chip.dataset.ieBound = '1';

      chip.addEventListener('click', function () {
        const name = this.dataset.filterName;

        const disclosure = document.querySelector('#ie-grid-header-filter .mobile-facets__disclosure');
        if (!disclosure) return;

        if (!disclosure.open) {
          disclosure.dataset.ieOpenedByChip = '1';

          const summary = disclosure.querySelector(':scope > summary');
          if (summary) summary.click();
        }

        const drawer = document.querySelector('#ie-grid-header-filter menu-drawer');
        if (!drawer) return;

        const target = drawer.querySelector(`.mobile-facets__details[data-filter-name="${name}"]`);
        if (!target) return;

        drawer.querySelectorAll('.mobile-facets__details').forEach((section) => {
          if (section === target) return;

          const header = section.querySelector('[onclick*="accordionFilterLogic"]');
          if (!header) return;

          const panel = header.nextElementSibling;
          if (!panel) return;

          const isOpen = !panel.classList.contains('tw-hidden');
          if (isOpen) {
            FacetFiltersForm.accordionFilterLogic(header);
          }
        });

        const header = target.querySelector('[onclick*="accordionFilterLogic"]');
        if (!header) return;

        const panel = header.nextElementSibling;
        if (panel && panel.classList.contains('tw-hidden')) {
          FacetFiltersForm.accordionFilterLogic(header);
        }
      });
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
window.accordionFilterLogic = FacetFiltersForm.accordionFilterLogic;

document.addEventListener('DOMContentLoaded', function () {
  if (typeof loadFacetsIcons === 'function') {
    loadFacetsIcons();
  }
  FacetFiltersForm.initHeaderInteractions();
});
